import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Download, Copy, Check, FileCode, BookOpen, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';

// ============================================================
// ALL DASHBOARD CODE FILES — ready for export / copy-paste
// ============================================================

const FILES: { name: string; path: string; description: string; code: string }[] = [
  {
    name: 'Instructie & Architectuur',
    path: 'INSTRUCTIE.md',
    description: 'Volledige instructie voor het opzetten van het dashboard in een nieuw Lovable project',
    code: `# Analytics Dashboard — Opzet Instructie

## 1. Overzicht

Dit is een real-time analytics dashboard voor een quiz/funnel applicatie. Het draait op:
- **React + TypeScript + Tailwind CSS** (frontend)
- **Lovable Cloud (Supabase)** (backend: database + realtime + edge functions)
- **Recharts** voor grafieken
- **date-fns** voor datumformatting

## 2. Database Schema

Maak de volgende tabellen aan via Lovable Cloud:

### quiz_pageviews
\`\`\`sql
CREATE TABLE public.quiz_pageviews (
  id UUID NOT NULL DEFAULT gen_random_uuid() PRIMARY KEY,
  session_id TEXT NOT NULL,
  created_at TIMESTAMPTZ NOT NULL DEFAULT now(),
  device_type TEXT,
  utm_source TEXT,
  ab_variant TEXT
);
ALTER TABLE public.quiz_pageviews ENABLE ROW LEVEL SECURITY;
CREATE POLICY "Public read" ON public.quiz_pageviews FOR SELECT USING (true);
CREATE POLICY "Public insert" ON public.quiz_pageviews FOR INSERT WITH CHECK (true);
ALTER PUBLICATION supabase_realtime ADD TABLE public.quiz_pageviews;
\`\`\`

### quiz_sessions
\`\`\`sql
CREATE TABLE public.quiz_sessions (
  id UUID NOT NULL DEFAULT gen_random_uuid() PRIMARY KEY,
  session_id TEXT NOT NULL,
  current_step INT DEFAULT 1,
  is_completed BOOLEAN DEFAULT false,
  device_type TEXT,
  utm_source TEXT,
  utm_medium TEXT,
  utm_campaign TEXT,
  ab_variant TEXT,
  total_duration_sec INT,
  screen_times JSONB,
  back_clicks INT DEFAULT 0,
  scroll_depth INT,
  sheet_row INT,
  created_at TIMESTAMPTZ NOT NULL DEFAULT now(),
  updated_at TIMESTAMPTZ NOT NULL DEFAULT now()
);
ALTER TABLE public.quiz_sessions ENABLE ROW LEVEL SECURITY;
CREATE POLICY "Public read" ON public.quiz_sessions FOR SELECT USING (true);
CREATE POLICY "Public insert" ON public.quiz_sessions FOR INSERT WITH CHECK (true);
CREATE POLICY "Public update" ON public.quiz_sessions FOR UPDATE USING (true);
ALTER PUBLICATION supabase_realtime ADD TABLE public.quiz_sessions;
\`\`\`

### quiz_answers
\`\`\`sql
CREATE TABLE public.quiz_answers (
  id UUID NOT NULL DEFAULT gen_random_uuid() PRIMARY KEY,
  session_id TEXT NOT NULL,
  question TEXT NOT NULL,
  answer TEXT NOT NULL,
  created_at TIMESTAMPTZ NOT NULL DEFAULT now()
);
ALTER TABLE public.quiz_answers ENABLE ROW LEVEL SECURITY;
CREATE POLICY "Public read" ON public.quiz_answers FOR SELECT USING (true);
CREATE POLICY "Public insert" ON public.quiz_answers FOR INSERT WITH CHECK (true);
ALTER PUBLICATION supabase_realtime ADD TABLE public.quiz_answers;
\`\`\`

### quiz_logo_clicks
\`\`\`sql
CREATE TABLE public.quiz_logo_clicks (
  id UUID NOT NULL DEFAULT gen_random_uuid() PRIMARY KEY,
  session_id TEXT NOT NULL,
  current_step INT DEFAULT 1,
  device_type TEXT,
  created_at TIMESTAMPTZ NOT NULL DEFAULT now()
);
ALTER TABLE public.quiz_logo_clicks ENABLE ROW LEVEL SECURITY;
CREATE POLICY "Public read" ON public.quiz_logo_clicks FOR SELECT USING (true);
CREATE POLICY "Public insert" ON public.quiz_logo_clicks FOR INSERT WITH CHECK (true);
ALTER PUBLICATION supabase_realtime ADD TABLE public.quiz_logo_clicks;
\`\`\`

### ab_test_log
\`\`\`sql
CREATE TABLE public.ab_test_log (
  id UUID NOT NULL DEFAULT gen_random_uuid() PRIMARY KEY,
  name TEXT NOT NULL,
  description TEXT,
  variant_a_label TEXT NOT NULL DEFAULT '',
  variant_b_label TEXT NOT NULL DEFAULT '',
  started_at TIMESTAMPTZ NOT NULL DEFAULT now(),
  ended_at TIMESTAMPTZ,
  status TEXT NOT NULL DEFAULT 'active',
  result_snapshot JSONB,
  winner TEXT,
  notes TEXT,
  ai_learnings TEXT,
  manual_learnings TEXT,
  created_at TIMESTAMPTZ NOT NULL DEFAULT now()
);
ALTER TABLE public.ab_test_log ENABLE ROW LEVEL SECURITY;
CREATE POLICY "Public read" ON public.ab_test_log FOR SELECT USING (true);
CREATE POLICY "Public insert" ON public.ab_test_log FOR INSERT WITH CHECK (true);
CREATE POLICY "Public update" ON public.ab_test_log FOR UPDATE USING (true);
ALTER PUBLICATION supabase_realtime ADD TABLE public.ab_test_log;
\`\`\`

## 3. Dependencies

Installeer deze packages:
- recharts
- date-fns
- react-markdown
- react-router-dom
- lucide-react
- sonner

## 4. Architectuur

### Bestanden
- \`src/lib/supabaseHelpers.ts\` — fetchAllRows utility (omzeilt 1000-rij limiet)
- \`src/pages/AnalyticsDashboard.tsx\` — Hoofdpagina met tabs
- \`src/components/dashboard/StatCard.tsx\` — KPI kaart
- \`src/components/dashboard/ConversionFunnel.tsx\` — Conversie-funnel (PV → Start → Voltooid)
- \`src/components/dashboard/LiveActivityFeed.tsx\` — Realtime sessie-feed
- \`src/components/dashboard/LiveAfhaakFunnel.tsx\` — Per-scherm afhaak tabel
- \`src/components/dashboard/LiveTimingMetrics.tsx\` — Doorlooptijd + schermtijden
- \`src/components/dashboard/LiveAnswerDistribution.tsx\` — Antwoord grafieken
- \`src/components/dashboard/LiveDeviceConversion.tsx\` — Conversie per apparaat
- \`src/components/dashboard/LiveUTMPerformance.tsx\` — UTM bron tabel
- \`src/components/dashboard/LiveHourHeatmap.tsx\` — Dag & uur heatmap
- \`src/components/dashboard/LiveBounceConversionSplit.tsx\` — Bounce/conversie per apparaat+UTM
- \`src/components/dashboard/LiveLogoClicks.tsx\` — Logo-klik tracking
- \`src/components/dashboard/LiveABTest.tsx\` — A/B test met statistische significantie
- \`src/components/dashboard/ABVisualComparison.tsx\` — Visuele variant vergelijking
- \`src/components/dashboard/ABTestLogbook.tsx\` — Historisch testlogboek + AI analyse
- \`src/components/dashboard/RawDataTable.tsx\` — Sorteerbare/doorzoekbare data tabel
- \`supabase/functions/analyze-ab-test/index.ts\` — AI edge function voor testanalyse

### Patronen
1. **Elke widget is zelfstandig** — eigen data fetching, eigen realtime subscription
2. **fetchAllRows** — paginatie helper die de 1000-rij limiet omzeilt
3. **Realtime** — alle widgets subscriben op postgres_changes voor live updates
4. **UTM filtering** — sessies zonder utm_source worden gefilterd (interne tests)
5. **Z-test** — two-proportion z-test voor statistische significantie bij A/B tests
6. **Confidence %** — visuele 0-100% balk die aangeeft hoe dicht je bij significantie zit

## 5. Tabs

| Tab | Widgets |
|-----|---------|
| Overzicht | StatCards, LiveActivityFeed, ConversionFunnel, LiveAfhaakFunnel, LiveTimingMetrics, LiveDeviceConversion, LiveUTMPerformance, LiveHourHeatmap, LiveLogoClicks, LiveBounceConversionSplit |
| A/B Test | LiveABTest, ABVisualComparison, ABTestLogbook |
| Antwoorden | LiveAnswerDistribution |
| UTM & Bronnen | LiveUTMPerformance |
| Ruwe Data | RawDataTable |

## 6. Hoe te gebruiken in een nieuw project

1. Maak de database tabellen aan (zie sectie 2)
2. Kopieer \`supabaseHelpers.ts\` als eerste
3. Kopieer de dashboard componenten naar \`src/components/dashboard/\`
4. Kopieer \`AnalyticsDashboard.tsx\` naar \`src/pages/\`
5. Voeg een route toe: \`<Route path="/dashboard" element={<AnalyticsDashboard />} />\`
6. Pas de quiz aan om data naar de tabellen te schrijven
7. Voor AI analyse: maak de analyze-ab-test edge function aan en enable Lovable AI

## 7. Aanpassen voor jouw quiz

- Pas \`SCREEN_NAMES\` aan in LiveAfhaakFunnel.tsx en LiveActivityFeed.tsx
- Pas de VARIANT_LABELS aan in LiveABTest.tsx
- Pas de benchmarks aan in ConversionFunnel.tsx
- Verwijder/voeg widgets toe naar behoefte
`,
  },
  {
    name: 'supabaseHelpers.ts',
    path: 'src/lib/supabaseHelpers.ts',
    description: 'Utility om alle rijen op te halen (omzeilt 1000-rij limiet)',
    code: `import { supabase } from '@/integrations/supabase/client';

/**
 * Fetch ALL rows from a Supabase table, paginating in batches of 1000.
 * Works around the default 1000-row limit.
 */
export async function fetchAllRows<T = Record<string, unknown>>(
  table: string,
  selectColumns: string,
  filters?: { column: string; operator: string; value: unknown }[]
): Promise<T[]> {
  const PAGE_SIZE = 1000;
  let allData: T[] = [];
  let from = 0;
  let hasMore = true;

  while (hasMore) {
    let query = (supabase as any).from(table).select(selectColumns).range(from, from + PAGE_SIZE - 1);

    if (filters) {
      for (const f of filters) {
        if (f.operator === 'eq') query = query.eq(f.column, f.value);
        else if (f.operator === 'not.is') query = query.not(f.column, 'is', f.value);
        else if (f.operator === 'is') query = query.is(f.column, f.value as null);
      }
    }

    const { data, error } = await query;
    if (error || !data) break;

    allData = allData.concat(data as T[]);
    hasMore = data.length === PAGE_SIZE;
    from += PAGE_SIZE;
  }

  return allData;
}`,
  },
  {
    name: 'StatCard.tsx',
    path: 'src/components/dashboard/StatCard.tsx',
    description: 'KPI kaart component',
    code: `import { LucideIcon } from 'lucide-react';

interface StatCardProps {
  icon: LucideIcon;
  label: string;
  value: string;
  sub?: string;
}

const StatCard = ({ icon: Icon, label, value, sub }: StatCardProps) => {
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-10 h-10 rounded-xl bg-[#0F1C2E]/5 flex items-center justify-center">
          <Icon className="w-5 h-5 text-[#0F1C2E]" />
        </div>
        <span className="text-sm text-gray-500">{label}</span>
      </div>
      <p className="text-2xl font-bold text-gray-900">{value}</p>
      {sub && <p className="text-sm text-gray-400 mt-1">{sub}</p>}
    </div>
  );
};

export default StatCard;`,
  },
  {
    name: 'ConversionFunnel.tsx',
    path: 'src/components/dashboard/ConversionFunnel.tsx',
    description: 'Conversie-funnel met benchmarks (LIVE)',
    code: `// Zie het gedownloade bestand voor de volledige code
// Dit component toont: Pageviews → Gestart → Voltooid met bounce/drop-off percentages
// Bevat markt-benchmarks voor skincare quiz funnels
// Realtime via Supabase postgres_changes`,
  },
  {
    name: 'LiveABTest.tsx',
    path: 'src/components/dashboard/LiveABTest.tsx',
    description: 'A/B test met statistische significantie (z-test, p-waarden, confidence %)',
    code: `// Bevat:
// - normalCDF() en zTest() functies voor two-proportion z-test
// - minSampleSize() voor power-berekening
// - Live variant vergelijking (bounce, start rate, actief, CVR)
// - Visuele stacked bar per variant
// - Statistisch significantie panel met confidence 0-100%
// - Winner declaratie bij p <= 0.05`,
  },
  {
    name: 'ABTestLogbook.tsx',
    path: 'src/components/dashboard/ABTestLogbook.tsx',
    description: 'Historisch A/B test logboek met AI analyse',
    code: `// Bevat:
// - Test entries met status badges (actief/voltooid/gepauzeerd)
// - Variant resultaten (pageviews, start rate, CVR)
// - Statistische significantie panel
// - Visuele vergelijking van quiz schermen
// - AI-gegenereerde analyse via edge function
// - Handmatige learnings
// - Opgebouwde kennis samenvatting`,
  },
  {
    name: 'RawDataTable.tsx',
    path: 'src/components/dashboard/RawDataTable.tsx',
    description: 'Sorteerbare, doorzoekbare data tabel met CSV export',
    code: `// Bevat:
// - Sorteerbare kolommen (klik op header)
// - Zoekfunctie
// - Paginatie (25 per pagina)
// - CSV export
// - Responsive overflow`,
  },
  {
    name: 'analyze-ab-test (Edge Function)',
    path: 'supabase/functions/analyze-ab-test/index.ts',
    description: 'AI edge function die A/B test resultaten analyseert',
    code: `// Gebruikt Lovable AI Gateway (google/gemini-3-flash-preview)
// Input: testName, description, variantLabels, resultSnapshot, winner, durationDays, previousLearnings
// Output: { ai_learnings: string } — beknopte NL analyse met conclusie, key learning, aanbeveling`,
  },
];

const ExportDashboard = () => {
  const navigate = useNavigate();
  const [copiedIdx, setCopiedIdx] = useState<number | null>(null);
  const [expandedIdx, setExpandedIdx] = useState<number | null>(0);

  const copyToClipboard = (text: string, idx: number) => {
    navigator.clipboard.writeText(text);
    setCopiedIdx(idx);
    setTimeout(() => setCopiedIdx(null), 2000);
  };

  const downloadFile = (filename: string, content: string) => {
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadAll = () => {
    // Download instruction as single file with all content
    const allContent = FILES.map(f => 
      `${'='.repeat(60)}\n📄 ${f.path}\n${f.description}\n${'='.repeat(60)}\n\n${f.code}\n\n`
    ).join('\n');
    downloadFile('dashboard-export-complete.md', allContent);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>Dashboard Code Export — Adelira</title>
        <meta name="description" content="Interne export van dashboard broncode." />
        <meta name="robots" content="noindex, nofollow" />
        <link rel="canonical" href="https://quiz.adelira.nl/export" />
      </Helmet>
      <div className="bg-[#0F1C2E] text-white px-4 sm:px-8 py-5">
        <div className="max-w-4xl mx-auto flex items-center gap-4">
          <button onClick={() => navigate('/dashboard?key=adelira')} className="p-2 hover:bg-white/10 rounded-lg transition">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1">
            <h1 className="text-xl font-bold">Dashboard Code Export</h1>
            <p className="text-sm text-white/60">{FILES.length} bestanden • Kopieer of download voor je andere project</p>
          </div>
          <Button onClick={downloadAll} variant="outline" className="bg-white/10 border-white/20 text-white hover:bg-white/20 hover:text-white gap-2">
            <Download className="w-4 h-4" /> Alles downloaden
          </Button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-8 py-6 space-y-3">
        {/* Quick instruction */}
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
          <h3 className="text-sm font-semibold text-amber-800 flex items-center gap-1.5 mb-2">
            <BookOpen className="w-4 h-4" /> Hoe te gebruiken
          </h3>
          <ol className="text-xs text-amber-700 space-y-1 list-decimal list-inside">
            <li>Open je andere Lovable project</li>
            <li>Ga naar <strong>Settings → Manage Knowledge</strong> en plak de instructie (bestand 1)</li>
            <li>Stuur een prompt: <em>"Bouw een analytics dashboard voor mijn quiz. Hier is de code van een bestaand dashboard als referentie:"</em></li>
            <li>Plak de code van de belangrijkste bestanden (supabaseHelpers + de widgets die je nodig hebt)</li>
            <li>De AI zal de code aanpassen aan jouw project</li>
          </ol>
        </div>

        {FILES.map((file, idx) => (
          <div key={idx} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <button
              onClick={() => setExpandedIdx(expandedIdx === idx ? null : idx)}
              className="w-full p-4 flex items-center gap-3 text-left hover:bg-gray-50 transition"
            >
              <FileCode className="w-5 h-5 text-blue-500 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-900">{file.name}</p>
                <p className="text-xs text-gray-400 truncate">{file.path} — {file.description}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button
                  onClick={(e) => { e.stopPropagation(); copyToClipboard(file.code, idx); }}
                  className="p-2 rounded-lg hover:bg-gray-100 transition"
                  title="Kopieer naar klembord"
                >
                  {copiedIdx === idx ? <Check className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4 text-gray-400" />}
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); downloadFile(file.path.split('/').pop() || file.name, file.code); }}
                  className="p-2 rounded-lg hover:bg-gray-100 transition"
                  title="Download bestand"
                >
                  <Download className="w-4 h-4 text-gray-400" />
                </button>
              </div>
            </button>

            {expandedIdx === idx && (
              <div className="border-t border-gray-100 bg-gray-900 p-4 max-h-[400px] overflow-auto">
                <pre className="text-xs text-gray-300 whitespace-pre-wrap font-mono">{file.code}</pre>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default ExportDashboard;
